import { useState, useEffect, useId } from 'react'
import PropTypes from 'prop-types'
import apiService from '../services/api'

const LoginModal = ({ isOpen, onClose, onLogin, onGetStarted, initialMode = 'login', initialInviteCode = '', initialEmail = '' }) => {
  const [isRegisterMode, setIsRegisterMode] = useState(initialMode === 'register')
  const [email, setEmail] = useState(initialEmail)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [inviteCode, setInviteCode] = useState(initialInviteCode)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setIsRegisterMode(initialMode === 'register')
      setError('') // Clear any previous errors when modal opens
      setEmail(initialEmail)
      setPassword('')
      setConfirmPassword('')
      setInviteCode(initialInviteCode)
    }
  }, [isOpen, initialMode, initialInviteCode, initialEmail])

  const getButtonText = () => {
    if (isLoading) {
      return isRegisterMode ? 'Creating Account...' : 'Signing In...'
    }
    return isRegisterMode ? 'Create Account' : 'Sign In'
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    if (!email.trim() || !password.trim()) {
      setError('Please enter both email and password')
      setIsLoading(false)
      return
    }

    if (isRegisterMode && password !== confirmPassword) {
      setError('Passwords do not match')
      setIsLoading(false)
      return
    }

    if (isRegisterMode && password.length < 8) {
      setError('Password must be at least 8 characters')
      setIsLoading(false)
      return
    }

    if (isRegisterMode && inviteCode.trim().length !== 12) {
      setError('Invite code must be exactly 12 characters')
      setIsLoading(false)
      return
    }

    try {
      if (isRegisterMode) {
        const response = await apiService.register(email.trim(), password.trim(), inviteCode.trim())
        // Store user info (token is now in HTTP-only cookie)
        localStorage.setItem('userEmail', response.email)
        localStorage.setItem('username', response.username)
        // Store token for admin check (mock mode returns token, prod uses HTTP-only cookie)
        if (response.token) {
          localStorage.setItem('authToken', response.token)
        }
        onLogin() // No token passed - it's in the cookie
      } else {
        const response = await apiService.login({ username: email.trim(), password: password.trim() })
        // Store user info (token is now in HTTP-only cookie)
        localStorage.setItem('userEmail', response.email || email.trim())
        localStorage.setItem('username', response.username)
        // Store token for admin check (mock mode returns token, prod uses HTTP-only cookie)
        if (response.token) {
          localStorage.setItem('authToken', response.token)
        }
        onLogin() // No token passed - it's in the cookie
      }
    } catch (error) {
      console.error(isRegisterMode ? 'Registration failed:' : 'Login failed:', error)
      if (error.message?.includes('already registered')) {
        setError('An account with this email already exists')
      } else {
        setError(isRegisterMode
          ? 'Registration failed. Please try again.'
          : 'Login failed. Please check your credentials and try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="rounded-2xl bg-gradient-to-br from-gray-700/80 to-gray-700/80 shadow-none outline -outline-offset-1 outline-white/10 relative overflow-hidden max-w-2xl w-full flex flex-col">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
        <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
        <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-white/10 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>
        
        <div className="px-5 py-6 sm:px-6 border-b border-white/10 relative z-10">
          <div className="p-4 relative z-10">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-3xl font-light tracking-tight text-white flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-6 text-white">
                    <path d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                {isRegisterMode ? 'Create Account' : 'Sign In'}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-6 sm:px-6 relative z-10">
          <div className="p-4 relative z-10">
            {(import.meta.env.VITE_MOCK_MODE === 'true' || import.meta.env.DEV) && (
              <div className="mb-6 bg-blue-900/30 border border-blue-800 rounded-xl p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-200">Development Mode</h3>
                    <div className="mt-2 text-sm text-blue-300">
                      <p>Use these test credentials:</p>
                      <ul className="mt-1 space-y-1">
                        <li><strong>demo@example.com</strong> / demo123</li>
                        <li><strong>testuser@example.com</strong> / test123</li>
                        <li><strong>admin@example.com</strong> / admin123</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-base font-medium text-gray-200 mb-2">
                  Email
                </label>
                <div className="relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    className="appearance-none block w-full px-4 py-3 border border-gray-600 rounded-xl shadow-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-900/50 backdrop-blur-sm transition-all duration-200 text-base text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Enter your email"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
              </div>

              {isRegisterMode && (
                <div>
                  <label htmlFor="inviteCode" className="block text-base font-medium text-gray-200 mb-2">
                    Invite Code
                  </label>
                  <div className="relative">
                    <input
                      id="inviteCode"
                      name="inviteCode"
                      type="text"
                      value={inviteCode}
                      onChange={(e) => setInviteCode(e.target.value)}
                      required
                      maxLength={12}
                      disabled={isLoading}
                      className="appearance-none block w-full px-4 py-3 border border-gray-600 rounded-xl shadow-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-900/50 backdrop-blur-sm transition-all duration-200 text-base text-white font-mono disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="xxxxxxxxxxxx"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-400">
                    Enter the 12-character invite code you received
                  </p>
                </div>
              )}

              <div>
                <label htmlFor="password" className="block text-base font-medium text-gray-200 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="appearance-none block w-full px-4 py-3 border border-gray-600 rounded-xl shadow-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-900/50 backdrop-blur-sm transition-all duration-200 text-base text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Enter your password"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                  </div>
                </div>
              </div>

              {isRegisterMode && (
                <div>
                  <label htmlFor="confirmPassword" className="block text-base font-medium text-gray-200 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={isLoading}
                      className="appearance-none block w-full px-4 py-3 border border-gray-600 rounded-xl shadow-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-900/50 backdrop-blur-sm transition-all duration-200 text-base text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="Confirm your password"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-900/30 border border-red-800 rounded-xl p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-base text-red-300">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-base font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                    {isLoading ? (
                      <svg className="animate-spin h-5 w-5 text-blue-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-blue-300 group-hover:text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                      </svg>
                    )}
                  </span>
                  {getButtonText()}
                </button>
              </div>
            </form>

            <div className="mt-6">
              <p className="text-center text-base text-gray-300">
                {isRegisterMode ? (
                  <>
                    Already have an account?{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setIsRegisterMode(false)
                        setError('')
                        setConfirmPassword('')
                        setInviteCode('')
                      }}
                      className="font-medium text-blue-400 hover:text-blue-300"
                    >
                      Sign in
                    </button>
                  </>
                ) : (
                  <>
                    Don&apos;t have an account?{' '}
                    <button
                      type="button"
                      onClick={onGetStarted}
                      className="font-medium text-blue-400 hover:text-blue-300"
                    >
                      Get started
                    </button>
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

LoginModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onLogin: PropTypes.func.isRequired,
  onGetStarted: PropTypes.func,
  initialMode: PropTypes.oneOf(['login', 'register']),
  initialInviteCode: PropTypes.string,
  initialEmail: PropTypes.string
}

export default LoginModal
